import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Building2, Wrench } from "lucide-react";
import type { BookingSheetFormProps } from "./types";

const DEFAULT_COLORS = ["#64748B", "#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"];

interface BookingSheetRecurringBlockFormProps extends BookingSheetFormProps {
  courts: Array<{ id: string; name: string }>;
  selectedCourtIds: string[];
  customers: any[];
  selectedCustomerId: string | null;
  onSelectCustomer: (id: string | null, name?: string) => void;
  customerSearch: string;
  onCustomerSearch: (v: string) => void;
  organizationId: string | null;
  standardPriceHint?: number | null;
  courtCount?: number;
}

export default function BookingSheetRecurringBlockForm({
  formState,
  onFieldChange,
  courts: _courts,
  selectedCourtIds,
  customers,
  selectedCustomerId,
  onSelectCustomer,
  customerSearch,
  onCustomerSearch,
  organizationId: _organizationId,
  standardPriceHint,
  courtCount,
}: BookingSheetRecurringBlockFormProps) {
  const { t } = useTranslation("admin");
  const DAY_NAMES = [t("bookingSheet.daySun"), t("bookingSheet.dayMon"), t("bookingSheet.dayTue"), t("bookingSheet.dayWed"), t("bookingSheet.dayThu"), t("bookingSheet.dayFri"), t("bookingSheet.daySat")];
  const {
    blockName,
    blockFrom,
    blockTo,
    blockDays,
    blockInterval,
    blockColor,
    blockComment,
    blockCourtIds,
    blockCategory,
    blockEstimatedValue,
    blockIsConfirmedRevenue,
  } = formState;

  const category = (blockCategory as string) || "revenue";
  const isRevenue = category === "revenue";

  const _activeCourtIds = (blockCourtIds as string[]) ?? selectedCourtIds;
  const selectedCustomer = customers.find((c: any) => c.id === selectedCustomerId);

  return (
    <div className="space-y-3 border-t border-border pt-3">
      {/* Name */}
      <div>
        <Label className="text-xs">{t("bookingSheet.nameRequired")} *</Label>
        <Input
          placeholder={t("bookingSheet.blockNamePlaceholder")}
          value={(blockName as string) ?? ""}
          onChange={(e) => onFieldChange("blockName", e.target.value)}
          className="h-9 text-sm"
        />
      </div>

      {/* Block category selector */}
      <div>
        <Label className="text-xs">{t("bookingSheet.blockType")}</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <button
            type="button"
            onClick={() => onFieldChange("blockCategory", "revenue")}
            className={cn(
              "flex items-center gap-2 p-2.5 rounded-lg border-2 transition-all text-left",
              isRevenue ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
            )}
          >
            <Building2 className="h-4 w-4 shrink-0 text-blue-600" />
            <div>
              <p className="text-xs font-medium">{t("bookingSheet.revenueGenerating")}</p>
              <p className="text-[10px] text-muted-foreground leading-tight">{t("bookingSheet.revenueGeneratingDesc")}</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => onFieldChange("blockCategory", "operational")}
            className={cn(
              "flex items-center gap-2 p-2.5 rounded-lg border-2 transition-all text-left",
              !isRevenue ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
            )}
          >
            <Wrench className="h-4 w-4 shrink-0 text-amber-600" />
            <div>
              <p className="text-xs font-medium">{t("bookingSheet.operational")}</p>
              <p className="text-[10px] text-muted-foreground leading-tight">{t("bookingSheet.operationalDesc")}</p>
            </div>
          </button>
        </div>
      </div>

      {/* Courts managed by parent via "Lägg till fler banor" in booking sheet header */}

      {/* Date range */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">{t("bookingSheet.startDate")}</Label>
          <Input type="date" value={(blockFrom as string) ?? ""} onChange={(e) => onFieldChange("blockFrom", e.target.value)} className="h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs">{t("bookingSheet.endDate")}</Label>
          <Input type="date" value={(blockTo as string) ?? ""} onChange={(e) => onFieldChange("blockTo", e.target.value)} className="h-9 text-sm" />
          <p className="text-[10px] text-muted-foreground mt-0.5">{t("bookingSheet.leaveEmptyOngoing")}</p>
        </div>
      </div>

      {/* Days of week */}
      <div>
        <Label className="text-xs">{t("bookingSheet.weekdays")}</Label>
        <div className="flex gap-1.5 mt-1">
          {DAY_NAMES.map((name, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                const days = (blockDays as number[]) ?? [];
                onFieldChange("blockDays", days.includes(i) ? days.filter((d: number) => d !== i) : [...days, i]);
              }}
              className={cn(
                "w-8 h-8 rounded-md text-xs font-medium border transition-colors",
                ((blockDays as number[]) ?? []).includes(i)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:bg-accent"
              )}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      {/* Interval */}
      <div>
        <Label className="text-xs">{t("bookingSheet.repeat")}</Label>
        <Select value={(blockInterval as string) ?? "1"} onValueChange={(v) => onFieldChange("blockInterval", v)}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">{t("bookingSheet.everyWeek")}</SelectItem>
            <SelectItem value="2">{t("bookingSheet.everyOtherWeek")}</SelectItem>
            <SelectItem value="3">{t("bookingSheet.every3rdWeek")}</SelectItem>
            <SelectItem value="4">{t("bookingSheet.every4thWeek")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Revenue fields (only for revenue blocks) */}
      {isRevenue && (
        <>
          <div>
            <Label className="text-xs">{t("bookingSheet.estimatedValuePerOccasion")}</Label>
            <Input
              value={(blockEstimatedValue as string) ?? ""}
              onChange={(e) => onFieldChange("blockEstimatedValue", e.target.value)}
              className="h-9 text-sm"
              placeholder="0"
            />
            {standardPriceHint != null && standardPriceHint > 0 && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {t("bookingSheet.standardCourtPrice")}: {Math.round(standardPriceHint)} kr ({courtCount ?? 1} {(courtCount ?? 1) === 1 ? t("bookingSheet.courtSingular") : t("bookingSheet.courtPlural")})
              </p>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs">{t("bookingSheet.actualRevenue")}</Label>
              <p className="text-[10px] text-muted-foreground">{t("bookingSheet.actualRevenueDesc")}</p>
            </div>
            <Switch
              checked={(blockIsConfirmedRevenue as boolean) ?? false}
              onCheckedChange={(v) => onFieldChange("blockIsConfirmedRevenue", v)}
            />
          </div>
        </>
      )}

      {/* Customer (only for revenue blocks) */}
      {isRevenue && (<div>
        <Label className="text-xs">{t("bookingSheet.customerOrganization")}</Label>
        <Input
          placeholder={t("bookingSheet.searchCustomerShort")}
          value={customerSearch}
          onChange={(e) => { onCustomerSearch(e.target.value); onSelectCustomer(null); }}
          className="h-9 text-sm"
        />
        {customers.length > 0 && !selectedCustomerId && customerSearch && (
          <div className="border rounded-md max-h-32 overflow-y-auto mt-1 shadow-sm">
            {customers.map((c: any) => (
              <button
                key={c.id}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent border-b last:border-0"
                onClick={() => onSelectCustomer(c.id, c.full_name || c.email)}
              >
                {c.full_name || c.email}
              </button>
            ))}
          </div>
        )}
        {selectedCustomer && (
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-xs">{selectedCustomer.full_name || selectedCustomer.email}</Badge>
            <button onClick={() => { onSelectCustomer(null); onCustomerSearch(""); }} className="text-xs text-muted-foreground hover:text-foreground">×</button>
          </div>
        )}
      </div>)}

      {/* Color */}
      <div>
        <Label className="text-xs">{t("bookingSheet.color")}</Label>
        <div className="flex gap-2 mt-1">
          {DEFAULT_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={cn(
                "h-6 w-6 rounded-full border-2 transition-all",
                (blockColor as string) === c ? "border-foreground scale-110" : "border-transparent"
              )}
              style={{ backgroundColor: c }}
              onClick={() => onFieldChange("blockColor", c)}
            />
          ))}
        </div>
      </div>

      {/* Comment */}
      <div>
        <Label className="text-xs">{t("bookingSheet.comment")}</Label>
        <Textarea
          value={(blockComment as string) ?? ""}
          onChange={(e) => onFieldChange("blockComment", e.target.value)}
          className="text-sm"
          rows={2}
          placeholder={t("bookingSheet.internalNote")}
        />
      </div>
    </div>
  );
}
